import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

// ============================================================================
// ModelMappingEditor
// ============================================================================
//
// Mirrors new-api's `/console/channel` model_mapping editor (see
// /data/workspace/com.github/new-api/web/src/features/channels/components/model-mapping-editor.tsx).
// Two tabs (可视化 /手动编辑), rows of (from, to) inputs with delete buttons,
// an "添加映射" Add button, a "填入模板" Fill Template button at the top right,
// datalist auto-complete for both columns from the active Key's Models,
// inline JSON error / duplicate-source alerts, and pretty-printed JSON
// serialization (JSON.stringify(obj, null, 2)) to match new-api's wire format.
//
// Props mirror new-api's ModelMappingEditor:
//   - value: the canonical JSON string (input/output)
//   - onChange: called on every edit with the new JSON string
//   - sourceModelOptions / targetModelOptions: datalist suggestions
//
// Validation matches new-api's validateModelMappingJson semantics: empty → OK,
// non-empty → must parse as JSON object with string→string pairs.

type ModelMappingEditorProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  sourceModelOptions?: string[];
  targetModelOptions?: string[];
};

type MappingRow = {
  id: string;
  from: string;
  to: string;
};

// Mirrors new-api's DUPLICATE_MAPPING_SENTINEL: when rows contain duplicate
// sources we set value to a sentinel string to block submit, since the
// server would reject anyway. Kept identical to upstream for cross-checking.
const DUPLICATE_MAPPING_SENTINEL = '{ "duplicate_source_models": ';

function getDuplicateSources(rows: MappingRow[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const row of rows) {
    const source = row.from.trim();
    if (!source) continue;
    if (seen.has(source)) {
      duplicates.add(source);
    } else {
      seen.add(source);
    }
  }
  return Array.from(duplicates);
}

// validateModelMappingJson mirrors new-api's behavior:
function validateModelMappingJson(modelMapping: string): { valid: boolean; error?: string } {
  if (!modelMapping || modelMapping.trim() === "") return { valid: true };
  let parsed: unknown;
  try {
    parsed = JSON.parse(modelMapping);
  } catch {
    return { valid: false, error: "Model mapping must be valid JSON format" };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { valid: false, error: "Model mapping must be a valid JSON object" };
  }
  for (const v of Object.values(parsed as Record<string, unknown>)) {
    if (typeof v !== "string") return { valid: false, error: "Model mapping values must be strings" };
  }
  return { valid: true };
}

// Parse JSON string into rows. Returns null on parse failure; sets error.
function parseJsonToRows(
  json: string,
  createRowId: () => string,
): { rows: MappingRow[]; error: string | null } {
  const trimmed = json.trim();
  if (!trimmed) return { rows: [], error: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { rows: [], error: "Model mapping must be valid JSON format" };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { rows: [], error: "Model mapping must be a valid JSON object" };
  }
  const entries = Object.entries(parsed as Record<string, unknown>);
  const invalidValue = entries.find(([, v]) => typeof v !== "string");
  if (invalidValue) return { rows: [], error: "Model mapping values must be strings" };
  const rows: MappingRow[] = entries.map(([from, to]) => ({
    id: createRowId(),
    from,
    to: String(to),
  }));
  return { rows, error: null };
}

// Convert rows back to JSON. Pretty-printed (matches new-api).
function convertRowsToJson(rows: MappingRow[]): string {
  if (rows.length === 0) return "";
  const obj: Record<string, string> = {};
  rows.forEach((row) => {
    if (row.from.trim()) obj[row.from.trim()] = row.to.trim();
  });
  return JSON.stringify(obj, null, 2);
}

export function ModelMappingEditor(props: ModelMappingEditorProps) {
  const sourceListId = useId();
  const targetListId = useId();
  const [mode, setMode] = useState<"visual" | "json">("visual");
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [jsonValue, setJsonValue] = useState(props.value);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const nextRowIdRef = useRef(0);
  const duplicateSources = useMemo(() => getDuplicateSources(rows), [rows]);

  const createRowId = () => {
    nextRowIdRef.current += 1;
    return `mapping-${nextRowIdRef.current}`;
  };

  // Sync from external value (e.g. reset after submit, programmatic change).
  // Mirrors new-api's useEffect on props.value.
  useEffect(() => {
    setJsonValue(props.value);
    const { rows: parsed, error } = parseJsonToRows(props.value, createRowId);
    if (!error) setRows(parsed);
  }, [props.value]);

  const syncRows = (updatedRows: MappingRow[]) => {
    setRows(updatedRows);
    const duplicates = getDuplicateSources(updatedRows);
    if (duplicates.length > 0) {
      setJsonError("Duplicate source model mappings are not allowed");
      setJsonValue(DUPLICATE_MAPPING_SENTINEL);
      props.onChange(DUPLICATE_MAPPING_SENTINEL);
      return;
    }
    const json = convertRowsToJson(updatedRows);
    setJsonError(null);
    setJsonValue(json);
    props.onChange(json);
  };

  const handleAddRow = () => {
    syncRows([...rows, { id: createRowId(), from: "", to: "" }]);
  };

  const handleDeleteRow = (id: string) => {
    syncRows(rows.filter((row) => row.id !== id));
  };

  const handleRowChange = (id: string, field: "from" | "to", newValue: string) => {
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, [field]: newValue } : row,
    );
    syncRows(updatedRows);
  };

  const handleJsonChange = (newJson: string) => {
    setJsonValue(newJson);
    props.onChange(newJson);
    const { rows: parsed, error } = parseJsonToRows(newJson, createRowId);
    if (error) {
      setJsonError(error);
    } else {
      setJsonError(null);
      setRows(parsed);
    }
  };

  // Mirrors new-api exactly: single-key example rendered pretty.
  const handleFillTemplate = () => {
    const template = JSON.stringify({ "gpt-3.5-turbo": "gpt-3.5-turbo-0125" }, null, 2);
    setJsonValue(template);
    props.onChange(template);
    const { rows: parsed } = parseJsonToRows(template, createRowId);
    setJsonError(null);
    setRows(parsed);
  };

  const handleModeChange = (nextMode: string) => {
    if (nextMode !== "visual" && nextMode !== "json") return;
    if (nextMode === "json") {
      const duplicates = getDuplicateSources(rows);
      if (duplicates.length === 0) {
        const json = convertRowsToJson(rows);
        setJsonValue(json);
        props.onChange(json);
      }
      setMode("json");
      return;
    }
    // switching to visual — re-parse current jsonValue
    const { rows: parsed, error } = parseJsonToRows(jsonValue, createRowId);
    if (!error) setRows(parsed);
    setMode("visual");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded-md border bg-muted/40 p-0.5 text-sm">
          <button
            type="button"
            onClick={() => handleModeChange("visual")}
            disabled={props.disabled}
            className={`px-3 py-1 rounded-sm ${mode === "visual" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
          >
            可视化
          </button>
          <button
            type="button"
            onClick={() => handleModeChange("json")}
            disabled={props.disabled}
            className={`px-3 py-1 rounded-sm ${mode === "json" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
          >
            手动编辑
          </button>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto p-0 text-primary underline-offset-4 hover:underline"
          onClick={handleFillTemplate}
          disabled={props.disabled}
        >
          填入模板
        </Button>
      </div>

      {jsonError && (
        <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {jsonError}
        </div>
      )}
      {duplicateSources.length > 0 && (
        <div className="rounded-md border bg-muted px-3 py-2 text-xs">
          重复的 source 模型:{duplicateSources.join(", ")}
        </div>
      )}

      {mode === "visual" ? (
        <div className="space-y-2">
          {rows.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-sm font-medium text-muted-foreground">
                <div>原始模型</div>
                <div>替换为</div>
                <div className="w-10"></div>
              </div>
              {rows.map((row) => (
                <div key={row.id} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                  <input
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={row.from}
                    onChange={(e) => handleRowChange(row.id, "from", e.target.value)}
                    placeholder="gpt-3.5-turbo"
                    disabled={props.disabled}
                    list={sourceListId}
                  />
                  <input
                    className="h-10 rounded-md border bg-background px-3 text-sm"
                    value={row.to}
                    onChange={(e) => handleRowChange(row.id, "to", e.target.value)}
                    placeholder="gpt-3.5-turbo-0125"
                    disabled={props.disabled}
                    list={targetListId}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRow(row.id)}
                    disabled={props.disabled}
                    className="h-10 w-10"
                    aria-label="删除该映射"
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
              尚未配置模型映射，点击「添加映射」开始。
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddRow}
            disabled={props.disabled}
            className="w-full"
          >
            + 添加映射
          </Button>
        </div>
      ) : (
        <textarea
          className={`w-full rounded-md border bg-background px-3 py-2 text-sm font-mono ${
            jsonError ? "border-destructive" : ""
          }`}
          rows={6}
          value={jsonValue}
          onChange={(e) => handleJsonChange(e.target.value)}
          placeholder={'{"original-model": "replacement-model"}'}
          disabled={props.disabled}
          aria-invalid={Boolean(jsonError)}
          aria-label="模型映射"
        />
      )}

      {props.sourceModelOptions && props.sourceModelOptions.length > 0 && (
        <datalist id={sourceListId}>
          {props.sourceModelOptions.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      )}
      {props.targetModelOptions && props.targetModelOptions.length > 0 && (
        <datalist id={targetListId}>
          {props.targetModelOptions.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      )}
    </div>
  );
}
